import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const apiOrigin = new URL(API).origin;
const AuthContext = createContext(null);
const CONNECTION_ERROR = "Не удалось проверить сессию. Проверьте подключение и повторите проверку. Пароль менять не нужно.";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState("");
  const userRef = useRef(null);
  const epochRef = useRef(0);
  const flightRef = useRef(null);
  const redirectRef = useRef(false);
  const updateUser = useCallback(value => { userRef.current = value; setUser(value); }, []);

  // A module/provider error is not evidence that the user's session was revoked.
  // Verify our own session, then use the existing refresh cookie if needed.
  const probeSession = useCallback(() => {
    const epoch = epochRef.current;
    if (flightRef.current?.epoch === epoch) return flightRef.current.promise;
    const job = { epoch, promise: null };
    job.promise = (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true, timeout: 12000 });
        return { status: "valid", user: data, refreshed: false };
      } catch (first) {
        if (first.response?.status !== 401) return { status: "unknown" };
      }
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true, timeout: 12000 });
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true, timeout: 12000 });
        return { status: "valid", user: data, refreshed: true };
      } catch (error) {
        return { status: error.response?.status === 401 ? "invalid" : "unknown" };
      }
    })().finally(() => { if (flightRef.current === job) flightRef.current = null; });
    flightRef.current = job;
    return job.promise;
  }, []);

  useEffect(() => {
    const id = axios.interceptors.response.use(r => r, async error => {
      const config = error?.config || {};
      let url;
      try { url = new URL(config.url, config.baseURL || window.location.origin); } catch { return Promise.reject(error); }
      const ownApi = url.origin === apiOrigin && url.pathname.startsWith("/api/");
      const authRequest = /^\/api\/auth\//.test(url.pathname);
      if (error.response?.status !== 401 || !ownApi || authRequest || !userRef.current || config._sessionRetried) {
        return Promise.reject(error);
      }
      const epoch = epochRef.current;
      const result = await probeSession();
      if (epoch !== epochRef.current) return Promise.reject(error);
      if (result.status === "valid") {
        updateUser(result.user); setSessionError("");
        // Never replay writes/payments. Only a failed read may be retried once.
        if (result.refreshed && ["get", "head"].includes((config.method || "get").toLowerCase())) {
          return axios({ ...config, _sessionRetried: true });
        }
      } else if (result.status === "invalid" && userRef.current && !redirectRef.current) {
        epochRef.current += 1; redirectRef.current = true;
        updateUser(null);
        try { sessionStorage.setItem("session_expired", "1"); } catch { /* storage unavailable */ }
        const path = window.location.pathname;
        if (!/^\/(admin\/login|auth)\/?$/.test(path)) {
          window.location.replace(`${path.startsWith("/admin") || path.startsWith("/academy") ? "/admin/login" : "/auth"}?session_expired=1`);
        }
      }
      // Network/5xx: preserve the existing session and reject just this request.
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(id);
  }, [probeSession, updateUser]);

  const checkAuth = useCallback(async () => {
    const epoch = epochRef.current;
    setLoading(true);
    const result = await probeSession();
    if (epoch !== epochRef.current) return;
    if (result.status === "valid") { updateUser(result.user); setSessionError(""); }
    else if (result.status === "invalid") { updateUser(null); setSessionError(""); }
    else setSessionError(CONNECTION_ERROR);
    setLoading(false);
  }, [probeSession, updateUser]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = userData => {
    epochRef.current += 1; redirectRef.current = false;
    updateUser(userData); setSessionError(""); setLoading(false);
  };
  const logout = async () => {
    epochRef.current += 1;
    updateUser(null); setSessionError(""); setLoading(false);
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch { /* explicit local logout */ }
  };
  return <AuthContext.Provider value={{ user, loading, sessionError, login, logout, refreshUser: checkAuth }}>
    {children}
  </AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);